import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Share2, AlertTriangle, MapPin, Ship } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";
const PortStrikesArticle = () => {
  const {
    t
  } = useLanguage();
  const congestionData = [{
    date: t('article.strikes.chart.oct5'),
    rotterdam: 18,
    antwerp: 51
  }, {
    date: t('article.strikes.chart.oct8'),
    rotterdam: 25,
    antwerp: 67
  }, {
    date: t('article.strikes.chart.oct10'),
    rotterdam: 32,
    antwerp: 90
  }, {
    date: t('article.strikes.chart.oct15'),
    rotterdam: 28,
    antwerp: 75
  }, {
    date: t('article.strikes.chart.oct18'),
    rotterdam: 15,
    antwerp: 45
  }, {
    date: t('article.strikes.chart.oct22'),
    rotterdam: 8,
    antwerp: 22
  }];
  const shareArticle = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">{t('header.title')}</h1>
                <p className="text-blue-600">{t('header.subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <article className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-red-500">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {t('common.highRisk')}
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.strikes.title')}
            </h1>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{t('article.strikes.date')}</span>
                <span className="mx-2">•</span>
                <span>{t('article.strikes.readTime')}</span>
              </div>
              <Button onClick={shareArticle} variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                {t('common.share')}
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <img src="https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Rotterdam Antwerp port strikes" className="w-full rounded-lg shadow-lg sk-edit-loading h-[384px] object-cover" />
            
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              {t('article.strikes.lead')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.strikes.section1.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.strikes.section1.content')}
            </p>

            {/* Congestion Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Ship className="h-5 w-5 mr-2 text-red-600" />
                  {t('article.strikes.chart.title')}
                </CardTitle>
                <CardDescription>{t('article.strikes.chart.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={congestionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={value => [`${value} ships`, '']} />
                    <Line type="monotone" dataKey="rotterdam" stroke="#ef4444" strokeWidth={3} name="Rotterdam" />
                    <Line type="monotone" dataKey="antwerp" stroke="#f97316" strokeWidth={3} name="Antwerp" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.strikes.section2.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.strikes.section2.content')}
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-l-4 border-l-red-500">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-800">
                    <MapPin className="h-5 w-5 mr-2" />
                    Rotterdam Port
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('article.strikes.rotterdam.issue')}:</span>
                      <span className="font-semibold">{t('article.strikes.rotterdam.issueValue')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('article.strikes.rotterdam.duration')}:</span>
                      <span className="font-semibold">48 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('article.strikes.rotterdam.peakShips')}:</span>
                      <span className="font-semibold text-red-600">32 vessels</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center text-orange-800">
                    <MapPin className="h-5 w-5 mr-2" />
                    Antwerp Port
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('article.strikes.antwerp.issue')}:</span>
                      <span className="font-semibold">{t('article.strikes.antwerp.issueValue')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('article.strikes.antwerp.duration')}:</span>
                      <span className="font-semibold">13 days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('article.strikes.antwerp.peakShips')}:</span>
                      <span className="font-semibold text-orange-600">90 vessels</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.strikes.section3.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.strikes.section3.content')}
            </p>

            <div className="bg-red-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3">
                {t('article.strikes.economicImpact.title')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold text-red-800">€850M</div>
                  <div className="text-sm text-red-600">{t('article.strikes.economicImpact.dailyLoss')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-800">2.3M</div>
                  <div className="text-sm text-red-600">{t('article.strikes.economicImpact.affectedTEU')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-800">15%</div>
                  <div className="text-sm text-red-600">{t('article.strikes.economicImpact.delayIncrease')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-800">48h</div>
                  <div className="text-sm text-red-600">{t('article.strikes.economicImpact.avgDelay')}</div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.strikes.section4.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.strikes.section4.content')}
            </p>

            {/* Source Link */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8 border-l-4 border-l-red-500">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('article.source.title')}
              </h3>
              <p className="text-gray-600 mb-3">{t('article.source.description')}</p>
              <a href="https://mykn.kuehne-nagel.com/news/article/strikes-ease-in-rotterdam-and-antwerp" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                <span className="mr-2">⚓</span>
                {t('article.source.readOriginal')}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                {t('article.source.publisher')}: Kuehne+Nagel
              </p>
            </div>

            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">
                {t('article.strikes.recovery.title')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>{t('article.strikes.recovery.rotterdam')}:</span>
                  <Badge className="bg-green-500">{t('article.strikes.recovery.rotterdamStatus')}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t('article.strikes.recovery.antwerp')}:</span>
                  <Badge className="bg-yellow-500">{t('article.strikes.recovery.antwerpStatus')}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t('article.strikes.recovery.normalOps')}:</span>
                  <span className="font-semibold">7-10 days</span>
                </div>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t('article.relatedArticles')}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/article/aws-outage">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('article.aws.title')}</CardTitle>
                    <CardDescription>{t('article.aws.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/article/freight-rates">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.freight.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>;
};
export default PortStrikesArticle;